import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { API_URL } from "../../lib/utils";
import { toastApiError } from "../../lib/apiErrors";

// Define/repõe o PIN de um funcionário (admin/gerente) ou o próprio PIN (mode="own")
export const PinDialog = ({ open, onOpenChange, employee, mode = "employee", hasPin = false, onSaved }) => {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setPin(""); setConfirm(""); setCurrent(""); setPassword(""); };
  const valid = /^\d{4}$/.test(pin) && pin === confirm && new Set(pin).size > 1;

  const save = async () => {
    setBusy(true);
    try {
      if (mode === "own") {
        await axios.put(`${API_URL}/auth/me/pin`, { pin, current_pin: hasPin ? current : undefined, password: password || undefined }, { withCredentials: true });
      } else {
        await axios.put(`${API_URL}/employees/${employee.user_id}/pin`, { pin }, { withCredentials: true });
      }
      toast.success(mode === "own" ? "O seu PIN foi actualizado" : `PIN de ${employee.name} definido`);
      reset(); onSaved?.(); onOpenChange(false);
    } catch (e) { toastApiError(e, "Não foi possível guardar o PIN"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/employees/${employee.user_id}/pin`, { withCredentials: true });
      toast.success("PIN removido");
      reset(); onSaved?.(); onOpenChange(false);
    } catch (e) { toastApiError(e, "Erro ao remover PIN"); }
    finally { setBusy(false); }
  };

  const digits = (setter) => (e) => setter(e.target.value.replace(/\D/g, "").slice(0, 4));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-sm" data-testid="pin-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">{mode === "own" ? "O meu PIN" : `PIN de ${employee?.name || ""}`}</DialogTitle>
          <DialogDescription>PIN de 4 dígitos para entrar pelo ecrã de perfis. Não use 4 dígitos iguais.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {mode === "own" && (hasPin ? (
            <div className="space-y-1">
              <Label>PIN actual</Label>
              <Input inputMode="numeric" type="password" value={current} onChange={digits(setCurrent)} placeholder="••••" data-testid="pin-current-input" />
              <p className="text-xs text-muted-foreground">Ou confirme com a palavra-passe em baixo</p>
            </div>
          ) : null)}
          {mode === "own" && (
            <div className="space-y-1">
              <Label>Palavra-passe {hasPin ? "(alternativa)" : ""}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="pin-password-input" />
            </div>
          )}
          <div className="space-y-1">
            <Label>Novo PIN</Label>
            <Input inputMode="numeric" type="password" value={pin} onChange={digits(setPin)} placeholder="••••" className="text-center text-xl tracking-[0.6em]" data-testid="pin-new-input" />
          </div>
          <div className="space-y-1">
            <Label>Confirmar PIN</Label>
            <Input inputMode="numeric" type="password" value={confirm} onChange={digits(setConfirm)} placeholder="••••" className="text-center text-xl tracking-[0.6em]" data-testid="pin-confirm-input" />
            {confirm && pin !== confirm && <p className="text-xs text-destructive">Os PINs não coincidem</p>}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {mode === "employee" && hasPin ? (
            <Button variant="ghost" className="text-destructive" onClick={remove} disabled={busy} data-testid="pin-remove-btn">Remover PIN</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={save} disabled={!valid || busy || (mode === "own" && !current && !password)} data-testid="pin-save-btn">{busy ? "A guardar…" : "Guardar PIN"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
